import type { EmployeeProfileResponseDto } from "@/generated/model/employeeProfileResponseDto";
import { canEditSensitiveSections } from "@/lib/people";

import { AdditionalNotesSection } from "../sections/AdditionalNotesSection";
import { AllergiesSection } from "../sections/AllergiesSection";
import { ContactDetailsSection } from "../sections/ContactDetailsSection";
import { EmergencyContactSection } from "../sections/EmergencyContactSection";
import { HealthInformationSection } from "../sections/HealthInformationSection";
import { KeyInformationSection } from "../sections/KeyInformationSection";
import { MedicationSection } from "../sections/MedicationSection";
import { PersonalInformationSection } from "../sections/PersonalInformationSection";
import { WithheldSectionCard } from "../SensitiveSectionNotes";

interface ProfileDetailsTabProps {
  nurseryId: string;
  userId: string;
  profile: EmployeeProfileResponseDto;
}

/** A sensitive section the viewer may not see arrives as null and shows as withheld. */
export function ProfileDetailsTab({
  nurseryId,
  userId,
  profile,
}: ProfileDetailsTabProps) {
  const { canEdit } = profile.capabilities;
  const canEditSensitive = canEditSensitiveSections(profile.capabilities);

  return (
    <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="flex lg:col-span-2">
        <KeyInformationSection
          nurseryId={nurseryId}
          userId={userId}
          keyInformation={profile.keyInformation}
          canEdit={canEdit}
        />
      </div>
      <PersonalInformationSection
        nurseryId={nurseryId}
        userId={userId}
        personalInformation={profile.personalInformation}
        canEdit={canEdit}
      />
      <ContactDetailsSection
        nurseryId={nurseryId}
        userId={userId}
        contactDetails={profile.contactDetails}
        canEdit={canEdit}
      />
      <EmergencyContactSection
        nurseryId={nurseryId}
        userId={userId}
        emergencyContact={profile.emergencyContact}
        canEdit={canEdit}
      />
      {profile.allergies ? (
        <AllergiesSection
          nurseryId={nurseryId}
          userId={userId}
          allergies={profile.allergies}
          canEdit={canEditSensitive}
        />
      ) : (
        <WithheldSectionCard
          title="Allergies"
          testId="profile-withheld-allergies"
        />
      )}
      {profile.healthInformation ? (
        <HealthInformationSection
          nurseryId={nurseryId}
          userId={userId}
          healthInformation={profile.healthInformation}
          canEdit={canEditSensitive}
        />
      ) : (
        <WithheldSectionCard
          title="Health information"
          testId="profile-withheld-health-information"
        />
      )}
      {profile.medication ? (
        <MedicationSection
          nurseryId={nurseryId}
          userId={userId}
          medication={profile.medication}
          canEdit={canEditSensitive}
        />
      ) : (
        <WithheldSectionCard
          title="Medication details"
          testId="profile-withheld-medication"
        />
      )}
      <div className="flex lg:col-span-2">
        <AdditionalNotesSection
          nurseryId={nurseryId}
          userId={userId}
          additionalNotes={profile.additionalNotes}
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}
